/* eslint-disable import/no-cycle */
/* eslint-disable import/extensions */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { PortableText } from '@portabletext/react';

import { LocaleContext } from '@repo/contexts/LocaleContext';

import FeaturedUGCVideos from '@repo/ui-components/featured-ugc-videos/FeaturedUGCVideos';
import { openMavenoidChat } from '@repo/ui-components/openChatWidget';

import sharedStyles from '../styles';
import ContactForm from '../form/ContactForm';

import BlockBuilderAccordion from './types/BlockBuilderAccordion';
import BlockBuilderDealerData from './types/BlockBuilderDealerData';
import BlockBuilderFeatureRow from './types/BlockBuilderFeatureRow';
import BlockBuilderFeatureSection from './types/BlockBuilderFeatureSection';
import BlockBuilderImage from './types/BlockBuilderImage';
import BlockBuilderLinkGrid from './types/BlockBuilderLinkGrid/BlockBuilderLinkGrid';
import BlockBuilderImageBanner from './types/BlockBuilderImageBanner';
import BlockBuilderImageBannerCta from './types/BlockBuilderImageBannerCta';
import BlockBuilderPrivacyPolicy from './types/BlockBuilderPrivacyPolicy';
import BlockBuilderProductGrid from './types/BlockBuilderProductGrid';
import BlockBuilderProductsComparisonGrid from './types/BlockBuilderProductsComparisonGrid';
import BlockBuilderQuiz from './types/BlockBuilderQuiz';
import BlockBuilderContentCard from './types/BlockBuilderContentCard';
import BlockBuilderSizeChart from './types/BlockBuilderSizeChart';
import BlockBuilderTwoColumnLayout from './types/BlockBuilderTwoColumnLayout';
import BlockBuilderThreeColumnLayout from './types/BlockBuilderThreeColumnLayout';
import BlockBuilderComparisonChart from './types/BlockBuilderComparisonChart';
import BlockBuilderHubspotFormEmbed from './types/BlockBuilderHubspotFormEmbed';
import MediaGrid from './types/MediaGrid';
import VideoGallery from './types/VideoGallery';
import BlockBuilderHorizontalRule from './types/BlockBuilderHorizontalRule';
import BlockBuilderSectionAnchor from './types/BlockBuilderSectionAnchor';
import CardRow from './types/CardRow';
import CallToActionRow from './types/CallToActionRow';
import BlockBuilderTestimonial from './types/BlockBuilderTestimonial';
import BlockBuilderTestimonialCarousel from './types/BlockBuilderTestimonialCarousel';
import BlockBuilderSubscriptionGrid from './types/BlockBuilderSubscriptionGrid';
import BlockBuilderBirdeyeReviewWidget from './types/BlockBuilderBirdeyeReviewWidget';
import BlockBuilderImageWithHorizontalTextList from './types/BlockBuilderImageWithHorizontalTextList';
import BlockBuilderDynamicDealerPricing from './types/BlockBuilderDynamicDealerPricing/BlockBuilderDynamicDealerPricing';
import BlockBuilderProspectScheduler from './types/BlockBuilderProspectScheduler';
import BlockBuilderReferallEnrollmentForm from './types/BlockBuilderReferralForm/EnrollmentForm';
import BlockContent from './BlockContent';
import BlockBuilderKlaviyoEmbed from './blocks/BlockBuilderKlaviyoEmbed';

const Presentation = styled.div`
  ${({ presentation }) =>
    presentation === 'full-width' &&
    css`
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -50vw;
      margin-right: -50vw;
    `}
`;

const StyledParagraph = styled.p`
  &.small {
    font-size: ${sharedStyles.fontSize.sm};
  }

  &.large {
    font-size: ${sharedStyles.fontSize.lg};
  }

  font-size: ${sharedStyles.fontSize.base};
`;

const blockBuilderTestimonial = ({ value, getInternalLink }) => (
  <BlockBuilderTestimonial value={value} getInternalLink={getInternalLink} />
);

const blockBuilderTestimonialCarousel = ({ value }) => (
  <BlockBuilderTestimonialCarousel value={value} />
);

const blockBuilderAccordion = ({ value, getInternalLink }) => (
  <BlockBuilderAccordion value={value} getInternalLink={getInternalLink} />
);

const blockBuilderDealerData = ({ value }) => (
  <BlockBuilderDealerData value={value} />
);

const blockBuilderReusableBlock = ({ value, getInternalLink, Button }) => (
  <BlockBuilder
    blocks={value?.reusableBlockRef?.blocks}
    getInternalLink={getInternalLink}
    Button={Button}
  />
);

const blockBuilderFeatureRow = ({ value, getInternalLink }) => (
  <BlockBuilderFeatureRow value={value} getInternalLink={getInternalLink} />
);

const blockBuilderFeatureSection = ({ value, getInternalLink }) => (
  <BlockBuilderFeatureSection value={value} getInternalLink={getInternalLink} />
);

const blockBuilderFeaturedUGCVideos = ({ value }) => (
  <FeaturedUGCVideos value={value} />
);

const blockBuilderText = ({ value, getInternalLink }) => (
  <BlockContent blocks={value.text} getInternalLink={getInternalLink} />
);

const blockBuilderImage = ({ value: { presentation }, value }) => (
  <Presentation className="blockbuilder" presentation={presentation}>
    <BlockBuilderImage value={value} />
  </Presentation>
);

const blockContentImage = ({ value: { presentation }, value }) => (
  <Presentation className="blockbuilder" presentation={presentation}>
    <BlockBuilderImage value={value} />
  </Presentation>
);

const blockBuilderLinkGrid = ({ value, getInternalLink }) => (
  <BlockBuilderLinkGrid value={value} getInternalLink={getInternalLink} />
);

const blockBuilderImageBanner = ({ value }) => (
  <BlockBuilderImageBanner value={value} />
);

const blockBuilderImageBannerCta = ({ value, getInternalLink }) => (
  <BlockBuilderImageBannerCta value={value} getInternalLink={getInternalLink} />
);

const blockPrivacyPolicy = ({ value, getInternalLink }) => (
  <BlockBuilderPrivacyPolicy value={value} getInternalLink={getInternalLink} />
);

const blockBuilderProductGrid = ({ value }) => (
  <BlockBuilderProductGrid value={value} />
);

const blockBuilderProductsComparisonGrid = ({ value, getInternalLink }) => (
  <BlockBuilderProductsComparisonGrid
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderQuiz = ({ value, Button, getInternalLink }) => (
  <BlockBuilderQuiz
    value={value}
    Button={Button}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderContentCard = ({ value, getInternalLink }) => (
  <BlockBuilderContentCard value={value} getInternalLink={getInternalLink} />
);

const blockBuilderSizeChart = ({ value }) => (
  <BlockBuilderSizeChart value={value} />
);

const blockBuilderHubspotFormEmbed = ({ value, getInternalLink }) => (
  <BlockBuilderHubspotFormEmbed
    formId={value?.form?.id}
    heading={value?.heading}
    getInternalLink={getInternalLink}
  />
);

const mediaGrid = ({ value, getInternalLink }) => (
  <MediaGrid value={value} getInternalLink={getInternalLink} />
);

const videoGallery = ({ value }) => <VideoGallery value={value} />;

const blockBuilderBreak = ({ value }) =>
  value.style === 'Horizontal Rule' ? <BlockBuilderHorizontalRule /> : '';

const blockBuilderSectionAnchor = ({ value }) => (
  <BlockBuilderSectionAnchor value={value} />
);

const blockBuilderTwoColumnLayout = ({ value, getInternalLink }) => (
  <BlockBuilderTwoColumnLayout
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderThreeColumnLayout = ({ value, getInternalLink }) => (
  <BlockBuilderThreeColumnLayout
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderComparisonChart = ({ value, getInternalLink }) => (
  <BlockBuilderComparisonChart
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderReferallEnrollmentForm = ({ getInternalLink }) => (
  <BlockBuilderReferallEnrollmentForm getInternalLink={getInternalLink} />
);

const blockBuilderSubscriptionGrid = ({ value, getInternalLink }) => (
  <BlockBuilderSubscriptionGrid
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderBirdeyeReviewWidget = ({ value }) => (
  <BlockBuilderBirdeyeReviewWidget value={value} />
);

const blockBuilderImageWithHorizontalTextList = ({
  value,
  getInternalLink,
}) => (
  <BlockBuilderImageWithHorizontalTextList
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderDynamicDealerPricing = ({ value, getInternalLink }) => (
  <BlockBuilderDynamicDealerPricing
    value={value}
    getInternalLink={getInternalLink}
  />
);

const blockBuilderProspectScheduler = ({
  personalSubmitButtonLabel,
  dealerSubmitButtonLabel,
  appointmentSubmitButtonLabel,
}) => (
  <BlockBuilderProspectScheduler
    personalSubmitButtonLabel={personalSubmitButtonLabel}
    dealerSubmitButtonLabel={dealerSubmitButtonLabel}
    appointmentSubmitButtonLabel={appointmentSubmitButtonLabel}
  />
);

const blockFormEmbed = ({ value, getInternalLink }) => {
  const { formName } = value;
  if (formName === 'Contact Us') {
    return <ContactForm value={value} getInternalLink={getInternalLink} />;
  }

  console.error('Invalid form selected.');
  return null;
};

const cardRow = ({ value, getInternalLink }) => (
  <CardRow value={value} getInternalLink={getInternalLink} />
);

const callToActionRow = ({ value, getInternalLink, Button }) => (
  <CallToActionRow
    value={value}
    getInternalLink={getInternalLink}
    Button={Button}
  />
);

const blockBuilderKlaviyoEmbed = ({ value }) => (
  <BlockBuilderKlaviyoEmbed block={value} key={value._key} />
);

const inlineIcon = ({ value }) => (
  <img
    src={value?.icon?.asset?.url}
    width={value?.iconWidth || '16'}
    height={value?.iconHeight || '16'}
    alt="Inline Icon"
    style={{ marginRight: '.25rem' }}
  />
);

const internalLink = ({ value, children, getInternalLink }) => {
  const href = getInternalLink(value) || '/';
  return <a href={href}>{children}</a>;
};

const link = ({ children, value, getInternalLink, localeContext }) => {
  if (value?.linkType === 'disable') {
    return children;
  }

  if (value?.linkType === 'action' && value?.actionType) {
    const { actionType } = value;

    return (
      <button
        type="button"
        className="text-foreground-link underline hover:no-underline leading-6"
        onClick={() => {
          if (actionType === 'openChat') {
            openMavenoidChat();
          }

          if (actionType === 'openCookieSettings') {
            if (typeof window !== 'undefined') {
              window?.OneTrust?.ToggleInfoDisplay();
            }
          }

          if (actionType === 'openCountryPicker') {
            localeContext?.setCountryPickerActive(true);
          }
        }}
      >
        {children}
      </button>
    );
  }

  if (value?.linkType === 'internal') {
    const href =
      getInternalLink({
        ...value,
        reference: value?.internalLink,
      }) || '/';
    return <Link to={href}>{children}</Link>;
  }

  return value?.blank ? (
    <a href={value?.href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <a href={value?.href}>{children}</a>
  );
};

const normal = ({ children }) => <p>{children}</p>;

const blockquote = ({ children }) => <blockquote>{children}</blockquote>;

const h1 = ({ children }) => <h1>{children}</h1>;

const h2 = ({ children }) => <h2>{children}</h2>;

const h3 = ({ children }) => <h3>{children}</h3>;

const h4 = ({ children }) => <h4>{children}</h4>;

const h5 = ({ children }) => <h5>{children}</h5>;

const h6 = ({ children }) => <h6>{children}</h6>;

const bullet = ({ children }) => <ul className="list-inside">{children}</ul>;

const number = ({ children }) => <ol className="list-inside">{children}</ol>;

const small = ({ children }) => (
  <StyledParagraph className="small">{children}</StyledParagraph>
);

const large = ({ children }) => (
  <StyledParagraph className="large">{children}</StyledParagraph>
);

function BlockBuilder({
  blocks = [],
  getInternalLink = () => {},
  Button = () => {},
}) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';

  const getInternalLinkWithCurrency = props =>
    getInternalLink({
      ...props,
      currency,
    });

  const components = {
    types: {
      blockBuilderTestimonial: ({ value }) =>
        blockBuilderTestimonial({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderTestimonialCarousel: ({ value }) =>
        blockBuilderTestimonialCarousel({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderAccordion: ({ value }) =>
        blockBuilderAccordion({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderDealerData,
      blockBuilderReusableBlock: ({ value }) =>
        blockBuilderReusableBlock({
          value,
          getInternalLink: getInternalLinkWithCurrency,
          Button,
        }),
      blockBuilderFeatureRow: ({ value }) =>
        blockBuilderFeatureRow({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderFeatureSection: ({ value }) =>
        blockBuilderFeatureSection({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderFeaturedUGCVideos: ({ value }) =>
        blockBuilderFeaturedUGCVideos({
          value,
        }),
      blockBuilderText: ({ value }) =>
        blockBuilderText({ value, getInternalLink }),
      blockBuilderImage,
      blockContentImage,
      blockBuilderLinkGrid: ({ value }) =>
        blockBuilderLinkGrid({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderImageBanner,
      blockBuilderImageBannerCta: ({ value }) =>
        blockBuilderImageBannerCta({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockPrivacyPolicy: ({ value }) =>
        blockPrivacyPolicy({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderProductGrid,
      blockBuilderProductsComparisonGrid: ({ value }) =>
        blockBuilderProductsComparisonGrid({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),

      blockBuilderProspectScheduler: ({ value }) =>
        blockBuilderProspectScheduler(value),
      blockBuilderQuiz: ({ value }) =>
        blockBuilderQuiz({
          value,
          Button,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderContentCard: ({ value }) =>
        blockBuilderContentCard({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderSizeChart,
      blockBuilderHubspotFormEmbed: ({ value }) =>
        blockBuilderHubspotFormEmbed({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      mediaGrid: ({ value }) =>
        mediaGrid({ value, getInternalLink: getInternalLinkWithCurrency }),
      videoGallery,
      blockBuilderBreak,
      blockBuilderSectionAnchor,
      blockBuilderTwoColumnLayout: ({ value }) =>
        blockBuilderTwoColumnLayout({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderThreeColumnLayout: ({ value }) =>
        blockBuilderThreeColumnLayout({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderComparisonChart: ({ value }) =>
        blockBuilderComparisonChart({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockFormEmbed: ({ value }) =>
        blockFormEmbed({ value, getInternalLink: getInternalLinkWithCurrency }),
      blockBuilderKlaviyoEmbed: ({ value }) =>
        blockBuilderKlaviyoEmbed({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderSubscriptionGrid: ({ value }) =>
        blockBuilderSubscriptionGrid({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderBirdeyeReviewWidget,
      blockBuilderImageWithHorizontalTextList: ({ value }) =>
        blockBuilderImageWithHorizontalTextList({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      blockBuilderDynamicDealerPricing: ({ value }) =>
        blockBuilderDynamicDealerPricing({
          value,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      cardRow: ({ value }) =>
        cardRow({ value, getInternalLink: getInternalLinkWithCurrency }),
      callToActionRow: ({ value }) =>
        callToActionRow({
          value,
          getInternalLink: getInternalLinkWithCurrency,
          Button,
        }),
      inlineIcon,
      referralFormPlaceholder: () =>
        blockBuilderReferallEnrollmentForm({
          getInternalLink: getInternalLinkWithCurrency,
        }),
    },
    marks: {
      internalLink: ({ value, children }) =>
        internalLink({
          value,
          children,
          getInternalLink: getInternalLinkWithCurrency,
        }),
      link: ({ children, value }) =>
        link({
          children,
          value,
          getInternalLink: getInternalLinkWithCurrency,
          localeContext,
        }),
    },
    block: {
      normal,
      blockquote,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      small,
      large,
    },
    list: {
      bullet,
      number,
    },
  };
  return <PortableText value={blocks} components={components} />;
}

BlockBuilder.propTypes = {
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  // eslint-disable-next-line react/require-default-props
  getInternalLink: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  Button: PropTypes.func,
};

export default BlockBuilder;
